<template>
  <b-modal
    :id="modalId"
    :aria-label="modalTitle"
    modal-class="select-folder-modal select-products-dialog"
    centered
    @show="onModalShow"
    @hidden="resetModalState"
  >
    <template #modal-header="{ close }">
      <div class="select-products-dialog__heading">
        <h2 class="select-products-dialog__title">
          {{ modalTitle }}
        </h2>
        <button
          type="button"
          class="select-products-dialog__close"
          aria-label="Close"
          @click="close()"
        >
          <XIcon
            :width="16"
            :height="16"
          />
        </button>
      </div>
      <b-tabs
        v-show="viewTabs"
        pills
        class="c-file-browser select-products-dialog__tabs"
      >
        <b-tab
          title="Files"
          :active="currentTab === TABS.FILES"
          @click="navigate(TABS.FILES)"
        />
        <b-tab
          title="Products"
          :active="currentTab === TABS.PRODUCTS"
          @click="navigate(TABS.PRODUCTS)"
        />
        <b-tab
          title="AI Studio"
          :active="currentTab === TABS.AI_STUDIO"
          @click="navigate(TABS.AI_STUDIO)"
        />
        <b-tab
          title="Stock"
          :active="currentTab === TABS.STOCK"
          @click="navigate(TABS.STOCK)"
        />
        <b-tab
          v-if="isSalsifyAvailable"
          title="Salsify Products"
          :active="currentTab === TABS.SALSIFY_PRODUCTS"
          @click="navigate(TABS.SALSIFY_PRODUCTS)"
        />
        <b-tab
          v-if="isSalsifyAvailable"
          title="Salsify Assets"
          :active="currentTab === TABS.SALSIFY_ASSETS"
          @click="navigate(TABS.SALSIFY_ASSETS)"
        />
        <b-tab
          v-if="isBoxAvailable"
          title="Box Assets"
          :active="currentTab === TABS.BOX_ASSETS"
          @click="navigate(TABS.BOX_ASSETS)"
        />
        <b-tab
          title="Briefs"
          :active="currentTab === TABS.BRIEF"
          @click="navigate(TABS.BRIEF)"
        />
        <b-tab
          v-if="showLayouts"
          title="Layouts"
          :active="currentTab === TABS.LAYOUTS"
          @click="navigate(TABS.LAYOUTS)"
        />
      </b-tabs>
    </template>
    <SelectFolderModalContentView
      v-if="isFolderTab"
      :allow-multiple-selection="mode !== 'selectAndCreate'"
      :is-light-weight="isLightWeight"
      @onSelectedItems="(items) => selectedItems = items"
      @folder-changed="onFolderChanged"
    />
    <template v-if="isAiStudioTab">
      <div
        v-if="aiStudioFolderLoading"
        class="select-folder-modal__ai-studio-state"
      >
        <b-spinner small />
      </div>
      <SelectFolderModalContentView
        v-else-if="aiStudioFolderId"
        :key="`ai-studio-${aiStudioFolderId}`"
        :root-folder-id="aiStudioFolderId"
        root-folder-name="AI Studio"
        :allow-multiple-selection="mode !== 'selectAndCreate'"
        :is-light-weight="isLightWeight"
        @onSelectedItems="(items) => selectedItems = items"
      />
      <div
        v-else
        class="select-folder-modal__ai-studio-state"
      >
        No AI Studio images yet. Images you generate in AI Studio will appear here.
      </div>
    </template>
    <ExternalContentView
      v-if="isStockTab"
      :endpoint="stockEndpoint"
      v-bind="pickerGridProps"
      @onSelectedItems="(items) => selectedItems = items"
    >
      <template #toolbar-start>
        <b-tabs
          pills
          class="c-file-browser c-file-browser--stock"
        >
          <b-tab
            title="It'sRapid"
            :active="currentStockTab === TABS.ITS_RAPID_STOCK"
            @click="navigateStock(TABS.ITS_RAPID_STOCK)"
          />
          <b-tab
            title="Pixabay"
            :active="currentStockTab === TABS.PIXABAY_STOCK"
            @click="navigateStock(TABS.PIXABAY_STOCK)"
          />
        </b-tabs>
      </template>
    </ExternalContentView>
    <template v-if="isSalsifyTab">
      <ExternalContentView
        :endpoint="salsifyEndpoint"
        v-bind="pickerGridProps"
        @onSelectedItems="(items) => selectedItems = items"
        @autoConfirm="handleOkClick"
      />
    </template>
    <ExternalContentView
      v-if="isBoxTab"
      :endpoint="boxEndpoint"
      v-bind="pickerGridProps"
      navigable-folders
      @onSelectedItems="(items) => selectedItems = items"
    />
    <BriefsTabContentView
      v-if="isBriefTab"
      :default-brief-id="defaultBriefId"
      @onSelectedItems="(items) => selectedItems = items"
    />
    <LayoutsContentView
      v-if="isLayoutsTab"
      @onLayoutLayers="handleLayoutLayers"
    />
    <template #modal-footer="{ cancel }">
      <slot
        name="footer"
        :selected-items="selectedItems"
        :current-folder-id="currentFolderId"
        :cancel="cancel"
      >
        <div class="select-products-dialog__footer-meta">
          <b-form-checkbox
            v-if="showCreateLayers"
            v-model="createLayers"
            switch
          >
            Create Layers
          </b-form-checkbox>
          <span class="select-products-dialog__count">{{ selectedItems.length }} selected</span>
        </div>
        <div class="select-products-dialog__footer-actions">
          <button
            type="button"
            class="select-products-dialog__cancel"
            @click="cancel()"
          >
            Cancel
          </button>
          <button
            type="button"
            class="select-products-dialog__add"
            :disabled="addDisabled"
            @click="handleOkClick"
          >
            Add
          </button>
        </div>
      </slot>
    </template>
  </b-modal>
</template>
<script>
import { X as XIcon } from 'lucide-vue';
import { FILE_TYPE } from '../../../../constants/file-image-types';
import { PRODUCT_PICKER_COLUMNS, PRODUCT_PICKER_PAGE_SIZE } from '@frontend/constants/select-image-grid';
import SelectFolderModalContentView from './SelectFolderModalContentView.vue';
import ExternalContentView from './ExternalContentView.vue';
import LayoutsContentView from './LayoutsContentView.vue';
import BriefsTabContentView from './BriefsTabContentView.vue';
import { TABS } from '../constants';
import FileBrowser from '@frontend/services/api/file-browser';

const EXTERNAL_SOURCES = {
  SALSIFY: 'salsify',
  BOX: 'box',
};

export default {
  components: { SelectFolderModalContentView, ExternalContentView, LayoutsContentView, BriefsTabContentView, XIcon },
  props: {
    mode: {
      type: String,
      default: 'default'
    },
    viewTabs: {
      type: Boolean,
      default: false
    },
    isLightWeight: {
      type: Boolean,
      default: false
    },
    modalTitle: {
      type: String,
      default: 'Select Folder'
    },
    modalId: {
      type: String,
      default: 'select-folder-modal'
    },
    // Optional: open the modal directly on a given tab. Accepts a top-level tab
    // (e.g. 'salsify-products' / 'salsify-assets') or a Stock sub-tab.
    initialStockTab: {
      type: String,
      default: ''
    },
    showLayouts: {
      type: Boolean,
      default: true
    },
    showCreateLayers: {
      type: Boolean,
      default: false
    },
    defaultBriefId: {
      type: [String, Number],
      default: null
    },
    // Picks where to save rather than what to add: Add resolves with a single folder,
    // the picked one or else the one being browsed. (POR-14746)
    pickDestination: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedItems: [],
      createLayers: false,
      TABS,
      currentTab: TABS.FILES,
      currentStockTab: TABS.ITS_RAPID_STOCK,
      isSalsifyAvailable: false,
      isBoxAvailable: false,
      aiStudioFolderId: null,
      aiStudioFolderLoading: false,
      currentFolderId: null,
      currentFolderName: '',
    }
  },
  computed: {
    isFolderTab() {
      return [TABS.FILES, TABS.PRODUCTS].includes(this.currentTab);
    },
    isStockTab() {
      return this.currentTab === TABS.STOCK;
    },
    isAiStudioTab() {
      return this.currentTab === TABS.AI_STUDIO;
    },
    isSalsifyTab() {
      return [TABS.SALSIFY_ASSETS, TABS.SALSIFY_PRODUCTS].includes(this.currentTab);
    },
    isBoxTab() {
      return this.currentTab === TABS.BOX_ASSETS;
    },
    isLayoutsTab() {
      return this.currentTab === TABS.LAYOUTS;
    },
    isBriefTab() {
      return this.currentTab === TABS.BRIEF;
    },
    stockEndpoint() {
      if (this.currentStockTab === TABS.PIXABAY_STOCK) {
        return '/stock/retrieve-pixabay';
      }

      return '/stock/retrieve-itsrapid';
    },
    salsifyEndpoint() {
      return this.currentTab === TABS.SALSIFY_ASSETS
        ? '/salsify/retrieve-assets'
        : '/salsify/retrieve-products';
    },
    boxEndpoint() {
      return '/box/folder-content';
    },
    pickerGridProps() {
      return {
        pageSize: PRODUCT_PICKER_PAGE_SIZE,
        columns: PRODUCT_PICKER_COLUMNS,
        columnsMd: PRODUCT_PICKER_COLUMNS,
        fitRows: true,
        bandToolbar: true,
        labelPager: true,
      };
    },
    // A selected file is not a destination, so only a folder overrides the folder being
    // browsed — the same rule as the Save Thumbnail To dialog.
    destinationFolder() {
      const [selected] = this.selectedItems;

      if (selected && selected.type === FILE_TYPE.FOLDER) {
        return selected;
      }

      return this.currentFolderId
        ? { id: this.currentFolderId, name: this.currentFolderName, type: FILE_TYPE.FOLDER }
        : null;
    },
    addDisabled() {
      if (this.pickDestination) {
        return !this.destinationFolder;
      }

      if (!this.selectedItems.length) {
        return true;
      }

      return this.showCreateLayers && this.createLayers && this.selectedItems.length !== 1;
    },
  },

  async mounted () {
    // Auth-only endpoint — guests (e.g. shared brief editors) must not surface a toast.
    this.isSalsifyAvailable = await this.isExternalSourceAvailable(EXTERNAL_SOURCES.SALSIFY);
    this.isBoxAvailable = await this.isExternalSourceAvailable(EXTERNAL_SOURCES.BOX);
  },

  methods: {
    async handleOkClick() {
      if (this.pickDestination) {
        this.$emit('onSelectedItems', [this.destinationFolder]);
        this.$bvModal.hide(this.modalId);

        return;
      }

      if (this.selectedItems.length === 1 && this.selectedItems[0].type === FILE_TYPE.FOLDER) {
        this.$emit('onSelectedItems', this.selectedItems);
        this.$bvModal.hide(this.modalId);

        return;
      }

      let allFilesToAdd = [];

      for (const item of this.selectedItems) {
        if (item.type === FILE_TYPE.FOLDER) {
          const folderFiles = await this.getDirectFilesFromFolder(item.id);
          allFilesToAdd = [...allFilesToAdd, ...folderFiles];
        } else {
          allFilesToAdd.push(item);
        }
      }

      allFilesToAdd = await this.resolveBoxAssets(allFilesToAdd);

      if (this.showCreateLayers && this.createLayers && allFilesToAdd.length !== 1) {
        toastr.warning('Select one image to create layers');
        return;
      }

      const options = {
        createLayers: this.createLayers
      }
      this.$emit('onSelectedItems', allFilesToAdd, options);
      
      this.$bvModal.hide(this.modalId);
    },

    async resolveBoxAssets(items) {
      const boxItems = items.filter((item) => item && item.type === FILE_TYPE.BOX_FILE);

      if (!boxItems.length) {
        return items;
      }

      let urlById = new Map();

      try {
        const { data } = await FileBrowser.importBoxAssets(boxItems.map((item) => ({
          id: item.id,
          name: item.name,
          extension: item.extension,
          download_url: item.download_url,
        })));

        urlById = new Map(((data && data.files) || []).map((file) => [String(file.id), file.url]));
      } catch (error) {
        toastr.error('Failed to load the selected Box file(s).');

        return items.filter((item) => !item || item.type !== FILE_TYPE.BOX_FILE);
      }

      return items
        .map((item) => {
          if (!item || item.type !== FILE_TYPE.BOX_FILE) {
            return item;
          }

          const url = urlById.get(String(item.id));

          if (!url) {
            return null;
          }

          return {
            ...item,
            path: url,
            url,
            original_path: url,
            download_url: url,
            _boxResolved: true,
          };
        })
        .filter(Boolean);
    },

    async getDirectFilesFromFolder(folderId, pageNumber = 1, allFiles = []) {
      try {
        const response = await axios.get('/file/folder_content', {
          params: {
            folderId: folderId,
            searchKey: '',
            pageNumber: pageNumber,
            pageSize: 100,
            folder_id: 0
          }
        });

        const data = response.data;
        const items = data.items || [];

        for (const item of items) {
          if (item.id === null && item.is_root === true) {
            continue;
          }

          if (item.type !== FILE_TYPE.FOLDER && item.type !== FILE_TYPE.PSD) {
            allFiles.push(item);
          }
        }

        if (data.current_page < data.last_page) {
          return await this.getDirectFilesFromFolder(folderId, pageNumber + 1, allFiles);
        }

        return allFiles;
      } catch (error) {
        console.error('Error fetching folder contents:', error);
        toastr.error('Failed to fetch folder contents');
        return allFiles;
      }
    },
    handleLayoutLayers(payload) {
      this.$emit('onLayoutLayers', payload);
      this.$bvModal.hide(this.modalId);
    },

    navigate(tab) {
      if (this.currentTab === tab) {
        return;
      }

      this.currentTab = tab;
      this.selectedItems = [];

      if ([TABS.FILES, TABS.PRODUCTS].includes(tab)) {
        this.$store.commit('fileUploadImages/SET_TAB', tab);
      }

      // The AI Studio tab is the Files view rooted at the company's "AI Studio" folder. (POR-14659)
      if (tab === TABS.AI_STUDIO) {
        this.$store.commit('fileUploadImages/SET_TAB', TABS.FILES);
        this.loadAiStudioFolder();
      }
    },
    async loadAiStudioFolder() {
      this.aiStudioFolderLoading = true;
      try {
        const { data } = await axios.get('/networks/ai-studio-library/folder');
        this.aiStudioFolderId = (data && data.folder_id) || null;
      } catch (e) {
        this.aiStudioFolderId = null;
      } finally {
        this.aiStudioFolderLoading = false;
      }
    },
    navigateStock(tab) {
      if (this.currentStockTab === tab) {
        return;
      }

      this.currentStockTab = tab;
      this.selectedItems = [];
    },
    // resetModalState() only clears the selection for tabbed callers, so without this a reopened
    // dialog would start at Home while still reporting the previous selection and folder.
    onModalShow() {
      this.selectedItems = [];
      this.currentFolderId = null;
      this.currentFolderName = '';
      this.applyInitialStockTab();
    },
    onFolderChanged(folderId, folderName = '') {
      this.currentFolderId = folderId;
      this.currentFolderName = folderName;
    },
    // Deep-link: when opened with initialStockTab, jump straight to that tab.
    // Salsify Products/Assets are TOP-LEVEL tabs, so select them via currentTab;
    // It'sRapid/Pixabay are Stock sub-tabs. resetModalState() restores defaults.
    applyInitialStockTab() {
      if (!this.initialStockTab) {
        return;
      }
      if ([TABS.SALSIFY_PRODUCTS, TABS.SALSIFY_ASSETS, TABS.BOX_ASSETS].includes(this.initialStockTab)) {
        this.currentTab = this.initialStockTab;
      } else {
        this.currentTab = TABS.STOCK;
        this.currentStockTab = this.initialStockTab;
      }
      this.selectedItems = [];
    },
    resetModalState() {
      this.createLayers = false;
      if (this.viewTabs) {
        this.selectedItems = [];
        this.currentTab = TABS.FILES;
        this.currentStockTab = TABS.ITS_RAPID_STOCK;
        this.$store.commit('fileUploadImages/SET_TAB', TABS.FILES);
      }

      this.$emit('hidden');
    },
    async isExternalSourceAvailable(source) {
      try {
        const { data } = await axios.post(
          '/platforms/is-platform-available',
          { platform: source },
          { skipErrorToast: true },
        );

        return Boolean(data && data.available);
      } catch {
        return false;
      }
    }
  }
}
</script>
<style scoped>
.select-folder-modal__ai-studio-state { display: flex; align-items: center; justify-content: center; min-height: 220px; color: #64748b; font-size: 14px; text-align: center; padding: 24px; }

::v-deep .select-products-dialog .modal-dialog {
  width: calc(100% - 2rem);
  max-width: 48rem;
  height: 760px;
  max-height: calc(100vh - 2rem);
  min-height: 0 !important;
  margin: 1rem auto;
}

::v-deep .select-products-dialog .modal-dialog::before {
  display: none;
}

::v-deep .select-products-dialog .modal-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
}

::v-deep .select-products-dialog .modal-header {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 0 0 auto;
  gap: 8px;
  padding: 16px 24px 8px;
  border-bottom: 0;
}

::v-deep .select-products-dialog .modal-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

::v-deep .select-products-dialog .modal-body > * {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

::v-deep .select-products-dialog .modal-footer {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px 16px;
  border-top: 1px solid #e5e7eb;
}

::v-deep .select-products-dialog .modal-footer > * {
  margin: 0;
}

.select-products-dialog__heading {
  min-height: 24px;
  padding-right: 28px;
}

.select-products-dialog__title {
  margin: 0;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1.5;
  color: #0f172a;
}

.select-products-dialog__close {
  position: absolute;
  top: 16px;
  right: 16px;
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  color: #0f172a;
  opacity: 0.7;
  line-height: 1;
}

.select-products-dialog__close:hover {
  opacity: 1;
}

/* ::v-deep so a caller's #footer slot content can reuse these button styles. */
::v-deep .select-products-dialog__footer-meta,
::v-deep .select-products-dialog__footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.select-products-dialog__count {
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  color: #64748b;
}

::v-deep .select-products-dialog__cancel,
::v-deep .select-products-dialog__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
}

::v-deep .select-products-dialog__cancel {
  background: transparent;
  color: #0f172a;
}

::v-deep .select-products-dialog__cancel:hover {
  background: #f1f5f9;
}

::v-deep .select-products-dialog__add {
  background: #27bb99;
  color: #fff;
}

::v-deep .select-products-dialog__add:hover:not(:disabled) {
  background: #22a88a;
}

::v-deep .select-products-dialog__add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

::v-deep .select-products-dialog .folder-list-container,
::v-deep .select-products-dialog .external-content-view,
::v-deep .select-products-dialog .layouts-content-view,
::v-deep .select-products-dialog .briefs-tab-content-view,
::v-deep .select-products-dialog .briefs-list-pane,
::v-deep .select-products-dialog .brief-content-view {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  gap: 0;
}

::v-deep .select-products-dialog .folder-sticky-header {
  position: static;
  flex: 0 0 auto;
  padding: 0;
  background: #fff;
}

::v-deep .select-products-dialog .folder-scroll-area,
::v-deep .select-products-dialog .external-content-view__sections {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 500px;
  overflow: hidden;
  padding: 8px 24px 4px;
}

::v-deep .select-products-dialog .folder-sticky-footer {
  position: static;
  flex: 0 0 auto;
  margin-top: auto;
  padding: 4px 0 8px;
  background: #fff;
}

::v-deep .select-products-dialog .folder-drop-hint {
  flex: 0 0 auto;
  padding: 2px 24px 6px;
  font-size: 12px;
}

::v-deep .select-products-dialog .briefs-list-pane__list {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  margin: 8px 24px 12px;
}

::v-deep .select-products-dialog .brief-content-view__grid {
  max-height: 500px;
  overflow: hidden;
  padding: 8px 24px 4px;
}

::v-deep .select-products-dialog__tabs .tab-content,
::v-deep .select-products-dialog .c-file-browser--stock .tab-content {
  display: none;
}

::v-deep .select-products-dialog__tabs.c-file-browser {
  padding: 0;
  font-family: inherit;
}

::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills {
  width: max-content;
  max-width: 100%;
  height: 40px;
  margin: 0;
  padding: 4px;
  gap: 0;
  flex-wrap: nowrap;
  overflow-x: auto;
  background-color: rgba(241, 245, 249, 0.5);
  border-radius: 6px;
  box-shadow: none;
}

::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills .nav-item {
  display: flex;
}

::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills a.nav-link {
  padding: 6px 12px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
}

::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills a.nav-link:hover {
  background: rgba(255, 255, 255, 0.6);
  color: #0f172a;
}

::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills a.nav-link.active,
::v-deep .select-products-dialog__tabs.c-file-browser .nav-pills a.nav-link.active:hover {
  background: #fff;
  color: #0f172a;
  font-weight: 500;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  pointer-events: none;
}

::v-deep .select-products-dialog .c-file-browser--stock .nav-pills {
  width: max-content;
  margin: 0;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 6px;
  box-shadow: none;
  display: flex;
}

::v-deep .select-products-dialog .c-file-browser--stock .nav-pills a.nav-link {
  padding: 6px 12px;
  border: 0;
  border-radius: 2px;
  background: transparent;
  color: #64748b;
  font-weight: 500;
}

::v-deep .select-products-dialog .c-file-browser--stock .nav-pills a.nav-link.active {
  background: #fff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  pointer-events: none;
}

::v-deep .select-products-dialog .folders-grid,
::v-deep .select-products-dialog .c-item-grid {
  height: auto;
  max-height: 100%;
  margin: 0;
  padding: 0;
  gap: 12px;
  justify-items: stretch;
  align-content: start;
  grid-auto-rows: max-content;
  overflow: hidden;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder):hover {
  border-color: rgba(39, 187, 153, 0.4);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder).is-selected {
  border-color: #27bb99;
  box-shadow: 0 0 0 1px #27bb99;
}

::v-deep .select-products-dialog .layouts-content-view__grid {
  box-sizing: border-box;
  max-height: 500px;
  overflow: hidden;
  padding: 8px 24px 4px;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview {
  position: relative;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background: #f1f5f9 !important;
  aspect-ratio: 1 / 1;
  width: 100%;
  overflow: hidden;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview:hover,
::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview.is-actions-open,
::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview.active {
  border: 0 !important;
  box-shadow: none !important;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview--item,
::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview--item:hover,
::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__preview--item.active {
  position: absolute;
  inset: 0;
  background: #f1f5f9 !important;
  border-radius: 0 !important;
  height: 100%;
  width: 100%;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-file-browser__item {
  width: 100%;
  height: 100%;
  background: transparent !important;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-file-browser__item img {
  max-width: 100% !important;
  max-height: 100% !important;
  width: auto;
  height: auto;
  object-fit: contain;
}

/* The dialog has a fixed height, so this box stays height-capped: a 2-line
   clamp needs 38px inside the 32px cap and ends up slicing the second line.
   One line with an ellipsis; the full name is in the tooltip. */
::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__name {
  margin: 0 !important;
  padding: 8px 8px 0;
  min-height: 2rem;
  max-height: 2rem;
  overflow: hidden !important;
  font-family: inherit !important;
  font-size: 12px !important;
  font-weight: 500 !important;
  line-height: 1.25 !important;
  text-align: left !important;
  color: #0f172a;
  white-space: nowrap !important;
  text-overflow: ellipsis;
  display: block;
}

::v-deep .select-products-dialog .c-grid-view-item:not(.c-grid-view-item--folder) .c-grid-view-item__name:last-child {
  padding-bottom: 8px;
}

::v-deep .select-products-dialog .c-grid-view-item__external-id {
  padding: 2px 8px 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 10px;
  line-height: 1.4;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .select-products-dialog .c-grid-view-item__checkbox {
  appearance: none;
  -webkit-appearance: none;
  top: 6px;
  left: 6px;
  width: 20px;
  height: 20px;
  margin: 0;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  opacity: 0;
  display: block;
}

::v-deep .select-products-dialog .c-grid-view-item__preview--item:hover .c-grid-view-item__checkbox,
::v-deep .select-products-dialog .c-grid-view-item__checkbox:checked {
  opacity: 1;
  display: block;
}

::v-deep .select-products-dialog .c-grid-view-item__checkbox:checked {
  background: #27bb99 url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e") center / 12px no-repeat !important;
  border-color: #27bb99 !important;
}

::v-deep .select-products-dialog .c-pagination--label {
  margin: 4px auto 8px;
}

::v-deep .select-products-dialog .c-pagination__label,
::v-deep .select-products-dialog .c-pagination__step {
  font-family: inherit;
}
</style>
